import {
  useEffect,
  useMemo,
  useState,
  useContext,
  useCallback,
  useRef
} from 'react';
import { getAuth } from "firebase/auth";
import {
  getJobs,
  searchJobs
} from '../services/jobService';
import { useAuth } from "./AuthContext";
import { TokenContext } from "./tokenContext"; // import TokenContext
import { logout } from "../utils/firebase";
import { createPortal } from "react-dom";
import GlobalNavDrawer from "./GlobalNavDrawer";
import { db } from "../utils/firebase";
import {  doc, getDoc } from "firebase/firestore";
import JobCard from './JobCard';
import JobDetails from './JobDetails';
import EmployerDashboard from './EmployerDashboard';
import {
  JOB_BANK_ROLES,
  isEmployer,
  isCareerCoach,
  redirectToJobBankLogin,
  requestJobBankAccess,
  useJobBankRole
} from './jobBankRole';
import "./JobBank.css";

const auth = getAuth();

function requireJobBankLogin(destination) {

  if (!auth.currentUser) {

    sessionStorage.setItem(
      "postLoginRedirect",
      destination
    );

    window.location.href =
      "/jobbank/login";

    return false;
  }

  return true;
}

function normalizeText(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function getSearchText(job) {
  return normalizeText([
    job.title,
    job.company,
    job.location,
    job.city,
    job.province,
    job.employmentType,
    job.workplaceType,
    job.type
  ].filter(Boolean).join(' '));
}

function matchesSearch(job, query) {
  const terms = normalizeText(query)
    .split(' ')
    .filter(Boolean);

  if (terms.length === 0) {
    return true;
  }

  const searchText = getSearchText(job);

  return terms.every(term =>
    searchText.includes(term)
  );
}

function dedupeJobs(jobs) {
  return Array.from(
    new Map(
      jobs.map(job => [
        job.source === 'jobbank'
          ? `jobbank-${job.jobId || job.postingNumericId || job.url}`
          : `local-${job.id}`,
        job
      ])
    ).values()
  );
}

function isExpiredJob(job) {

  if (!job.postingDate) {
    return false;
  }

  const postingDate =
    new Date(job.postingDate);

  if (isNaN(postingDate.getTime())) {
    return false;
  }

  const expiryDate =
    new Date(postingDate);

  expiryDate.setDate(
    expiryDate.getDate() + 30
  );

  return expiryDate <= new Date();
}

function formatSalary(value) {

  if (!value) return '';

  const number =
    Number(
      String(value)
        .replace(/[^0-9.-]/g, '')
    );

  if (isNaN(number)) {
    return value;
  }

  return `$${number.toLocaleString()}`;
}

function getJobKey(job) {

  if (!job) {
    return '';
  }

  return [
    job.source || 'local',
    job.id ||
    job.jobId ||
    job.postingNumericId ||
    job.url ||
    `${job.title}-${job.company}-${job.location}`
  ].join('-');
}

export default function JobBank() {
  const { balance, setBalance } = useContext(TokenContext);
  const [mode, setMode] = useState('seeker');
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);

  const detailsPanelRef = useRef(null);
  const searchInputRef = useRef(null);
  const { currentUser } = useAuth();
  const { role, roleLoading } = useJobBankRole(currentUser);
  const isLoggedIn = !!currentUser;

  function requireEmployerAccess(destination = "/employer") {

    if (!currentUser) {
      redirectToJobBankLogin(destination);
      return false;
    }

    if (roleLoading) {
      alert("Checking your Job Bank role. Please try again in a moment.");
      return false;
    }

    if (!isEmployer(role)) {
      requestJobBankAccess({
        requestedRole: JOB_BANK_ROLES.EMPLOYER,
        requestedAccess: "Employer Profile",
        accessKey: "employer-profile",
        sourcePath: destination
      });
      return false;
    }

    return true;
  }

  function requireCareerCoachAccess(destination = "/cecp") {

    if (!currentUser) {
      redirectToJobBankLogin(destination);
      return false;
    }

    if (roleLoading) {
      alert("Checking your Job Bank role. Please try again in a moment.");
      return false;
    }

    if (!isCareerCoach(role)) {
      requestJobBankAccess({
        requestedRole: JOB_BANK_ROLES.CAREER_COACH,
        requestedAccess: "Career Coach Portal",
        accessKey: "career-coach-portal",
        sourcePath: destination
      });
      return false;
    }

    return true;
  }

  function getDisplayJob(job) {
    if (isLoggedIn || !job) {
      return job;
    }

    return {
      ...job,
      company: "Sign in to view company"
    };
  }

  function scrollDetailsToTop() {

    requestAnimationFrame(() => {

      const panel = detailsPanelRef.current;

      if (!panel) {
        return;
      }

      panel.scrollTop = 0;

      panel.scrollTo({
        top: 0,
        left: 0,
        behavior: 'auto'
      });
    });
  }

  function selectJob(job) {

    setSelectedJob(job);

    scrollDetailsToTop();
  }

  useEffect(() => {
    scrollDetailsToTop();
  }, [selectedJob]);

  const loadJobBankJobs = useCallback(async () => {

    try {

      const response = await fetch(
        'https://demo.ispeakwell.ca/api/jobbank/combined'
      );

      const data = await response.json();

      console.log(
        'Job Bank jobs loaded:',
        data
      );

      if (!Array.isArray(data)) {

        console.error(
          'Endpoint did not return an array:',
          data
        );

        return [];
      }

      return data
        .filter(job => {

          const sections =
            job.sections || {};

          const hasDetails =
            Object.keys(sections).length > 0;

          const notExpired =
            !isExpiredJob(job);

          return (
            hasDetails &&
            notExpired
          );
        })
        .map(job => ({
          ...job,
          source: 'jobbank'
        }));

    } catch (err) {

      console.error(
        'Failed to load Job Bank jobs:',
        err
      );

      return [];
    }

  }, []);

  const loadJobs = useCallback(async () => {

    setLoading(true);

    try {

      const firestoreJobs =
        await getJobs();

      const normalizedFirestoreJobs =
        firestoreJobs.map(job => ({
          ...job,
          source: 'local',
          salary: formatSalary(job.salary)
        }));

      const jobBankJobs =
        await loadJobBankJobs();

      const allJobs = [
        ...normalizedFirestoreJobs,
        ...jobBankJobs
      ];

      const uniqueJobs =
        dedupeJobs(allJobs);
      
      setJobs(uniqueJobs);

      if (uniqueJobs.length > 0) {
        setSelectedJob(uniqueJobs[0]);
      }

    } catch (err) {

      console.error(
        'loadJobs failed:',
        err
      );

    } finally {

      setLoading(false);
    }
  }, [loadJobBankJobs]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  async function handleSearch(value) {

    setQuery(value);

    if (!value.trim()) {
      return loadJobs();
    }

    try {

      const firestoreJobs =
        await searchJobs(value);

      const normalizedFirestoreJobs =
        firestoreJobs.map(job => ({
          ...job,
          source: 'local',
          salary: formatSalary(job.salary)
        }));

      const jobBankJobs =
        await loadJobBankJobs();

      const filteredFirestoreJobs =
      normalizedFirestoreJobs.filter(job =>
        matchesSearch(job, value)
      );

    const filteredJobBankJobs =
      jobBankJobs.filter(job =>
        matchesSearch(job, value)
      );

    const results =
      dedupeJobs([
        ...filteredFirestoreJobs,
        ...filteredJobBankJobs
      ]);

    setJobs(results);

    setSelectedJob(
      results.length > 0
        ? results[0]
        : null
    );

    } catch (err) {

      console.error(
        'Search failed:',
        err
      );
    }
  }

  const employerJobs = useMemo(() => {
    return jobs.filter(job => job.source === 'local');
  }, [jobs]);

  useEffect(() => {
    let isMounted = true;

    const fetchTokens = async () => {
      if (!currentUser?.uid) return;

      const userRef = doc(db, "users", currentUser.uid);

      try {
        const userDoc = await getDoc(userRef);

        if (userDoc.exists() && isMounted) {
          setBalance(userDoc.data().tokens ?? 0);
        }
      } catch (error) {
        console.error("Error fetching token count:", error);
      }
    };

    fetchTokens();

    return () => {
      isMounted = false;
    };
  }, [currentUser, setBalance]);
  
  return (
    <div className="job-bank-page">

      <header className="job-bank-header">

        <div>
          <p className="job-bank-label">
            Spruce Grove Local Job Bank
          </p>

          <h1>
            Local jobs. Local employers.
          </h1>
        </div>

        <div className="job-bank-mode-buttons">

          <button
            className={mode === 'seeker' ? 'active' : ''}
            onClick={() => setMode('seeker')}
          >
            Job Seeker
          </button>

          <button
            className={mode === 'employer' ? 'active' : ''}
            onClick={() => {
              if (
                requireEmployerAccess(
                  "/employer"
                )
              ) {
                setMode("employer");
              }

            }}
          >
            Employer
          </button>
          <button onClick={() => {
              if (
                requireCareerCoachAccess(
                  "/cecp"
                )
              ) {
                window.location.href =
                  "/cecp";
              }

            }}
          >
            Career Coach
          </button>

          <button onClick={() => {

              if (
                requireJobBankLogin(
                  "/events"
                )
              ) {
                window.location.href =
                  "/events";
              }

            }}
          >
            Hiring Events
          </button>
        </div>

      </header>

      {
        mode === 'seeker'
          ? (
            <div className="job-bank-layout">

              <div className="job-list-panel">

                <div className="job-search-box">

                  <div className="job-search-row">

                    <input
                      ref={searchInputRef}
                      value={query}
                      onChange={(e) => handleSearch(e.target.value)}
                      placeholder="Search jobs, skills, companies..."
                    />

                    {
                      query && (
                        <button
                          className="search-clear-icon"
                          onClick={() => {

                            setQuery('');

                            loadJobs();

                            requestAnimationFrame(() => {
                              searchInputRef.current?.focus();
                            });
                          }}
                          title="Clear Search"
                        >
                          ✕
                        </button>
                      )
                    }

                  </div>

                  <p>
                    {jobs.length} job{jobs.length !== 1 ? 's' : ''} found
                  </p>

                </div>

                <div className="job-cards-scroll">

                  {
                    loading
                      ? (
                        <div className="job-loading">
                          Loading jobs...
                        </div>
                      )
                      : (
                        jobs.map(job => {

                          const jobKey =
                            getJobKey(job);

                          return (
                            <JobCard
                              key={jobKey}
                              job={getDisplayJob(job)}
                              selected={
                                getJobKey(selectedJob) === jobKey
                              }
                              onClick={selectJob}
                            />
                          );
                        })
                      )
                  }

                </div>

              </div>

              <div
                className="job-details-panel"
                ref={detailsPanelRef}
              >
                {
                  <JobDetails
                    key={getJobKey(selectedJob)}
                    job={selectedJob}
                  />
                }
              </div>

            </div>
          )
          : (
            <EmployerDashboard
              jobs={employerJobs}
              reloadJobs={loadJobs}
            />
          )
      }
      {createPortal(
        <GlobalNavDrawer
          hideCurrent
          links={[
            { to: "/purchase", label: `Tokens: ${balance}`},
            { to: "/jobbank", label: "Job Bank Home"},
            { to: "/voicebot", label: "iSpeakWell" },
            { to: "/resumerevisor", label: "Resumes" },
            { to: "/conversationpractice", label: "Convos" },
            { to: "/interview-practice", label: "Interviews" },
            { to: "/termsofservice", label: "Terms" },
            { to: "/privacypolicy", label: "Privacy" },
            { to: "/admin", label: "Admin" },
            { to: "/login", label: "Logout", onClick: async () => { await logout(); } },
          ]}
        />,
          document.body
      )}
    </div>
  );
}